import React, { createContext, useContext, useMemo, useState } from 'react';
import { autoUpdate, FloatingFocusManager, FloatingPortal, offset, shift, useClick, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { MoreVert } from '@mui/icons-material';
import clsx from 'clsx';
import { IconButton, IconButtonProps } from '../Button/IconButton';
import { Link, LinkProps } from '../Link/Link';

type MenuButtonProps = {
	icon?: React.ReactNode;
	size?: IconButtonProps['size'];
	tooltip?: string | [string, string];
	children: React.ReactNode;
};

type MenuContextType = {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
};
const MenuContext = createContext<MenuContextType>({
	isOpen: false,
	setIsOpen: () => {},
});

export const MoreMenu = React.forwardRef<HTMLButtonElement, MenuButtonProps>(({
	icon,
	size,
	children,
	...props
}, ref) => {
	const [isOpen, setIsOpen] = useState(false);

	const { refs, floatingStyles, context } = useFloating({
		open: isOpen,
		onOpenChange: setIsOpen,
		placement: 'bottom',
		middleware: [offset({ mainAxis: 4 }), shift()],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context);
	const dismiss = useDismiss(context);
	const role = useRole(context);

	const { getReferenceProps, getFloatingProps } = useInteractions([
		click,
		dismiss,
		role,
	]);

	const menuContext = useMemo(() => ({
		isOpen,
		setIsOpen,
	}), [isOpen, setIsOpen]);

	return (
		<>
			<IconButton
				ref={(el) => {
					refs.setReference(el);
					ref?.(el);
				}}
				size={size}
				icon={icon ?? <MoreVert />}
				active={isOpen}
				{...props}
				{...getReferenceProps()}
			/>
			{isOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context}>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							{...getFloatingProps()}
							className="flex w-max min-w-0 flex-col gap-0 rounded-md border border-borders-primary bg-bg-static-surface_default p-[10px] shadow-[0px_4px_7px_0px_rgba(25,25,25,0.05)]"
						>
							<MenuContext.Provider value={menuContext}>
								{children}
							</MenuContext.Provider>
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</>
	);
});

type MenuItemProps = {
	icon?: React.ReactNode;
	disabled?: boolean;
	children: React.ReactNode;
	className?: string;
} & LinkProps & React.ButtonHTMLAttributes<HTMLButtonElement>;

export function MenuItem({
	icon,
	children,
	onClick,
	className,
	disabled,
	...props
}: MenuItemProps) {
	const { isOpen, setIsOpen } = useContext(MenuContext);

	const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		if (disabled) {
			return;
		}
		setIsOpen(false);
		onClick?.(e);
	};

	return (
		<Link
			unstyled
			className={clsx(
				'flex w-full items-center gap-[10px] rounded-[4px] p-2 text-sm leading-5 text-text-primary hover:bg-bg-interactive-light-bg-active_hover',
				disabled && 'text-text-tertiary hover:text-text-tertiary cursor-default',
				className,
			)}
			onClick={handleClick}
			{...props}
		>
			<span className="*:size-[18px]!">{icon}</span>
			{children}
		</Link>
	);
}
